/**
 * The account's database tokens.
 *
 * Owned by the signed-in user rather than by the workspace, so there is one
 * list for the account and no workspace in the key. Nothing broadcasts a token
 * -- there is no realtime event for one -- so every write patches the list from
 * its own response, which is what keeps two tabs from disagreeing.
 */
import {
  queryOptions,
  useMutation,
  useQuery,
  useQueryClient,
  type QueryClient,
} from '@tanstack/react-query'

import { getApi, unwrap } from '@/api/client'
import { qk } from '@/api/query-keys'
import type { DatabaseToken, DatabaseTokenPermissions } from '@/api/types'

export function databaseTokensQuery() {
  return queryOptions({
    queryKey: qk.databaseTokens(),
    queryFn: (): Promise<DatabaseToken[]> =>
      unwrap(getApi().GET('/api/database/tokens/')),
  })
}

export function useDatabaseTokens() {
  return useQuery(databaseTokensQuery())
}

function store(queryClient: QueryClient, token: DatabaseToken): void {
  queryClient.setQueryData<DatabaseToken[]>(qk.databaseTokens(), (held) => {
    if (held === undefined) return held
    const known = held.some((one) => one.id === token.id)
    return known
      ? held.map((one) => (one.id === token.id ? token : one))
      : [...held, token]
  })
}

export function useCreateDatabaseToken() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({
      name,
      workspaceId,
    }: {
      name: string
      workspaceId: number
    }): Promise<DatabaseToken> =>
      unwrap(
        getApi().POST('/api/database/tokens/', {
          body: { name, workspace: workspaceId },
        }),
      ),
    onSuccess: (token) => {
      store(queryClient, token)
    },
  })
}

/** What a `PATCH` may change. `rotate_key` is required by the serializer. */
export interface DatabaseTokenUpdate {
  name?: string
  permissions?: DatabaseTokenPermissions
  rotate_key?: boolean
}

export function useUpdateDatabaseToken() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({
      tokenId,
      values,
    }: {
      tokenId: number
      values: DatabaseTokenUpdate
    }): Promise<DatabaseToken> =>
      unwrap(
        getApi().PATCH('/api/database/tokens/{token_id}/', {
          params: { path: { token_id: tokenId } },
          body: { ...values, rotate_key: values.rotate_key ?? false },
        }),
      ),
    // Applied from the response rather than from what was sent, so a permission
    // the backend rewrote -- an empty list where `false` went out -- is shown as
    // it was stored, and a failed write leaves the held token untouched.
    onSuccess: (token) => {
      store(queryClient, token)
    },
  })
}

export function useDeleteDatabaseToken() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (tokenId: number): Promise<unknown> =>
      unwrap(
        getApi().DELETE('/api/database/tokens/{token_id}/', {
          params: { path: { token_id: tokenId } },
        }),
      ),
    onSuccess: (_answer, tokenId) => {
      queryClient.setQueryData<DatabaseToken[]>(qk.databaseTokens(), (held) =>
        held?.filter((one) => one.id !== tokenId),
      )
    },
  })
}
